// Shared client-side password policy — single source of truth for the console.
//
// This MIRRORS the server-side GoTrue policy configured in
// infra/terraform/cloud_run_gotrue.tf (GOTRUE_PASSWORD_MIN_LENGTH +
// GOTRUE_PASSWORD_REQUIRED_CHARACTERS). GoTrue is the authoritative enforcement
// point; this module only gives users immediate, specific feedback so they don't
// have to round-trip to discover the rules (T276983548).
//
// GoTrue's GOTRUE_PASSWORD_REQUIRED_CHARACTERS is a colon-separated list of
// character groups; a password must contain at least one character from EACH
// non-empty group. The groups below are kept identical to the Terraform default
// so client and server agree exactly — including the symbol set, which omits the
// ':' separator (and '\\' / '"') to avoid escaping pitfalls. A symbol outside
// this set (e.g. ':' or '"') does NOT satisfy the symbol requirement on either
// side.

export const MIN_PASSWORD_LENGTH = 12

// Must stay in sync with var.gotrue_password_required_characters in
// infra/terraform/variables.tf.
const SYMBOL_CHARS = '!@#$%^&*()_+-=[]{}|;,.<>?'

interface PasswordRule {
  id: string
  label: string
  test: (pw: string) => boolean
}

const hasFrom = (chars: string) => (pw: string) => pw.split('').some((c) => chars.includes(c))

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'length',
    label: `At least ${MIN_PASSWORD_LENGTH} characters`,
    test: (pw) => pw.length >= MIN_PASSWORD_LENGTH,
  },
  {
    id: 'lowercase',
    label: 'A lowercase letter',
    test: (pw) => /[a-z]/.test(pw),
  },
  {
    id: 'uppercase',
    label: 'An uppercase letter',
    test: (pw) => /[A-Z]/.test(pw),
  },
  {
    id: 'digit',
    label: 'A number',
    test: (pw) => /[0-9]/.test(pw),
  },
  {
    id: 'symbol',
    label: 'A symbol (e.g. !@#$%)',
    test: hasFrom(SYMBOL_CHARS),
  },
]

/** Human-readable requirement list, e.g. for placeholder/help text. */
export const PASSWORD_REQUIREMENTS: string[] = PASSWORD_RULES.map((r) => r.label)

export interface PasswordValidationResult {
  ok: boolean
  /** Labels of the rules that FAILED (empty when ok). */
  errors: string[]
  /** IDs of the rules that FAILED (stable, for logging/telemetry). */
  failedRuleIds: string[]
}

/**
 * Validate a password against the full policy. Use this on signup, password
 * change, and password reset — NOT on sign-in (existing users may have shorter
 * legacy passwords and must still be able to log in; the server never enforces
 * complexity at login).
 */
export function validatePassword(password: string): PasswordValidationResult {
  const failed = PASSWORD_RULES.filter((rule) => !rule.test(password))
  return {
    ok: failed.length === 0,
    errors: failed.map((r) => r.label),
    failedRuleIds: failed.map((r) => r.id),
  }
}

/** Single-line summary suitable for an inline error message. */
export function describePasswordError(result: PasswordValidationResult): string {
  if (result.ok) return ''
  return `Password must contain: ${result.errors.join(', ')}.`
}

/** Map a password-rule ID to its i18n key (e.g. "length" → "passwordRuleLength"). */
export function getPasswordRuleTranslationKey(id: string): string {
  return `passwordRule${id.charAt(0).toUpperCase()}${id.slice(1)}`
}

/**
 * Build a translated password-error message from a validation result.
 * Accepts the `t` function from `useTranslation('auth')`.
 */
export function translatePasswordError(
  t: (key: string, opts?: Record<string, string | number>) => string,
  result: { ok: boolean; failedRuleIds: string[] },
): string {
  if (result.ok) return ''
  const rules = result.failedRuleIds.map((id) => {
    const key = getPasswordRuleTranslationKey(id)
    return id === 'length' ? t(key, { count: MIN_PASSWORD_LENGTH }) : t(key)
  })
  return t('passwordMustContain', { rules: rules.join(', ') })
}
